// Auth state for the curator SPA.
// On mount, hits /auth/me to determine if there's a live session (cookie
// already set by a previous login). Login + logout call /auth/web-login and
// /auth/web-logout respectively; the cookie is set/cleared by the server.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { Profile } from "@globalvillage/shared";
import { api, ApiError } from "../lib/api";

interface AuthState {
  user: Profile | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Profile | null>(null);
  // `loading: true` on first mount so the app can render a splash while we
  // probe for an existing session. Subsequent state changes set it false.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Bootstrap: ask the API who we are. If the cookie is missing or expired,
  // /auth/me returns 401 and we land on the login screen.
  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        const data = await api<{ user: Profile }>("/auth/me", {
          signal: ctrl.signal,
        });
        setUser(data.user);
      } catch (err) {
        if ((err as any)?.name === "AbortError") return;
        // 401 here is expected — just means "no session". Anything else is
        // an unexpected failure we want to know about.
        if (!(err instanceof ApiError) || err.status !== 401) {
          console.error("[auth] /me probe failed:", err);
        }
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setError(null);
    try {
      const data = await api<{ user: Profile }>("/auth/web-login", {
        method: "POST",
        body: { email, password },
      });
      setUser(data.user);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Something went wrong. Please try again.");
      }
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api<{ ok: true }>("/auth/web-logout", { method: "POST" });
    } finally {
      setUser(null);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
